import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "./services/api";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.png";
import useSettings from "./hooks/useSettings";
import ServiceWorkerUpdate from "./components/ServiceWorkerUpdate";
import QuotaExceededModal from "./components/QuotaExceededModal";
import { HEADER_CONFIG_SETTING_KEY, parseHeaderConfig } from "./utils/headerConfig";
import { getSystemFontFamily, loadSystemFonts } from "./utils/systemFont";

const queryClient = new QueryClient();

const App = () => {
  // A página pública não deve carregar a identidade visual da sessão interna.
  // Além de desnecessário, isso tentava buscar logos relativos a /webchat/:slug.
  const isPublicWebchatPortal = window.location.pathname.startsWith("/webchat/");
  const [locale, setLocale] = useState();
  const defaultPrimaryLight =
    localStorage.getItem("primaryColorLight") || "#1E3A8A";
  const defaultPrimaryDark =
    localStorage.getItem("primaryColorDark") || "#60A5FA";
  const appNameLocalStorage = localStorage.getItem("appName") || "Lead Pro";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(defaultPrimaryLight);
  const [primaryColorDark, setPrimaryColorDark] = useState(defaultPrimaryDark);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const [systemFont, setSystemFont] = useState(() => parseHeaderConfig().systemFont);
  const [fontOverrides, setFontOverrides] = useState(() => parseHeaderConfig());
  const { getPublicSetting } = useSettings();
  const [navbarHeight, setNavbarHeight] = useState(56);
  const [navbarPosition, setNavbarPosition] = useState("fixed");
  const [navbarBackground, setNavbarBackground] = useState("");
  const publicCompanyToken = new URLSearchParams(window.location.search).get(
    "companyToken"
  );

  const loadAssetWithFallback = useCallback((file, fallback, setter) => {
    if (file) {
      const assetUrl = getBackendUrl() + "/public/" + file;
      const img = new Image();
      img.onload = () => setter(assetUrl);
      img.onerror = () => setter(fallback);
      img.src = assetUrl;
      return;
    }
    setter(fallback);
  }, []);

  const loadPublicBranding = useCallback(async () => {
    try {
      const [
        colorLight,
        colorDark,
        appLogoLightFile,
        appLogoDarkFile,
        appLogoFaviconFile,
        publicAppName,
        publicNavbarHeight,
        publicNavbarPosition,
        publicNavbarBackground
      ] = await Promise.all([
        getPublicSetting("primaryColorLight", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("primaryColorDark", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("appLogoLight", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("appLogoDark", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("appLogoFavicon", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("appName", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("navbarHeight", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("navbarPosition", {
          companyToken: publicCompanyToken || undefined,
        }),
        getPublicSetting("navbarBackground", {
          companyToken: publicCompanyToken || undefined,
        })
      ]);

      setPrimaryColorLight(colorLight || "#1E3A8A");
      setPrimaryColorDark(colorDark || "#60A5FA");
      loadAssetWithFallback(appLogoLightFile, defaultLogoLight, setAppLogoLight);
      loadAssetWithFallback(appLogoDarkFile, defaultLogoDark, setAppLogoDark);
      loadAssetWithFallback(
        appLogoFaviconFile,
        defaultLogoFavicon,
        setAppLogoFavicon
      );
      setAppName(publicAppName || "Lead Pro");
      setNavbarHeight(Number(publicNavbarHeight) > 0 ? Number(publicNavbarHeight) : 56);
      setNavbarPosition(publicNavbarPosition || "fixed");
      setNavbarBackground(publicNavbarBackground || "");
    } catch (error) {
      console.log("Error loading public branding", error);
    }
  }, [getPublicSetting, loadAssetWithFallback, publicCompanyToken]);

  const loadCompanyBrandingFromSession = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const { data } = await api.get("/settings");
      const settings = Array.isArray(data) ? data : [];
      const valueByKey = (key) =>
        settings.find((s) => s && s.key === key)?.value || "";

      const companyColorLight = valueByKey("primaryColorLight");
      const companyColorDark = valueByKey("primaryColorDark");
      const companyLogoLight = valueByKey("appLogoLight");
      const companyLogoDark = valueByKey("appLogoDark");
      const companyFavicon = valueByKey("appLogoFavicon");
      const companyAppName = valueByKey("appName");
      const companyNavbarHeight = valueByKey("navbarHeight");
      const companyNavbarPosition = valueByKey("navbarPosition");
      const companyNavbarBackground = valueByKey("navbarBackground");
      const companyHeaderConfig = valueByKey(HEADER_CONFIG_SETTING_KEY);

      if (companyColorLight) setPrimaryColorLight(companyColorLight);
      if (companyColorDark) setPrimaryColorDark(companyColorDark);
      if (companyLogoLight)
        loadAssetWithFallback(companyLogoLight, defaultLogoLight, setAppLogoLight);
      if (companyLogoDark)
        loadAssetWithFallback(companyLogoDark, defaultLogoDark, setAppLogoDark);
      if (companyFavicon)
        loadAssetWithFallback(
          companyFavicon,
          defaultLogoFavicon,
          setAppLogoFavicon
        );
      if (companyAppName) setAppName(companyAppName);
      if (Number(companyNavbarHeight) > 0) {
        setNavbarHeight(Number(companyNavbarHeight));
      }
      if (companyNavbarPosition) setNavbarPosition(companyNavbarPosition);
      if (companyNavbarBackground) setNavbarBackground(companyNavbarBackground);
      const headerConfig = parseHeaderConfig(companyHeaderConfig);
      setSystemFont(headerConfig.systemFont);
      setFontOverrides(headerConfig);
    } catch (error) {
      console.log("Error loading company branding", error);
    }
  }, [loadAssetWithFallback]);

  useEffect(() => {
    const handleHeaderConfigUpdate = (event) => {
      const headerConfig = parseHeaderConfig(event.detail);
      setSystemFont(headerConfig.systemFont);
      setFontOverrides(headerConfig);
    };
    window.addEventListener("header-config-updated", handleHeaderConfigUpdate);
    return () => window.removeEventListener("header-config-updated", handleHeaderConfigUpdate);
  }, []);

  useEffect(() => {
    const fontFamily = getSystemFontFamily(systemFont);
    const titleFont = getSystemFontFamily(fontOverrides.systemTitleFont || systemFont);
    const menusFont = getSystemFontFamily(fontOverrides.menusFont || systemFont);
    const messagesFont = getSystemFontFamily(fontOverrides.messagesFont || systemFont);
    const announcementsFont = getSystemFontFamily(fontOverrides.announcementsFont || systemFont);
    loadSystemFonts([systemFont, fontOverrides.systemTitleFont, fontOverrides.menusFont, fontOverrides.messagesFont, fontOverrides.announcementsFont]);
    document.documentElement.style.setProperty("--system-font-family", fontFamily);
    document.documentElement.style.setProperty("--system-title-font-family", titleFont);
    document.documentElement.style.setProperty("--menus-font-family", menusFont);
    document.documentElement.style.setProperty("--messages-font-family", messagesFont);
    document.documentElement.style.setProperty("--announcements-font-family", announcementsFont);
  }, [systemFont, fontOverrides]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode);

          const root = document.documentElement;
          root.style.setProperty(
            "--primaryColor",
            newMode === "light" ? primaryColorLight : primaryColorDark
          );

          return newMode;
        });
      },
      setPrimaryColorLight: (color) => {
        setPrimaryColorLight(color);

        if (mode === "light") {
          const root = document.documentElement;
          root.style.setProperty("--primaryColor", color);
        }
      },
      setPrimaryColorDark: (color) => {
        setPrimaryColorDark(color);

        if (mode === "dark") {
          const root = document.documentElement;
          root.style.setProperty("--primaryColor", color);
        }
      },
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      setNavbarHeight,
      setNavbarPosition,
      setNavbarBackground,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      navbarHeight,
      navbarPosition,
      navbarBackground,
      mode,
    }),
    [
      mode,
      primaryColorLight,
      primaryColorDark,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      navbarHeight,
      navbarPosition,
      navbarBackground,
    ]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.1)",
              backgroundColor: mode === "light" ? "#E0E0E0" : "#424242",
              borderRadius: "4px",
            },
          },
          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark,
              light: mode === "light" ? primaryColorLight : primaryColorDark,
              dark: mode === "light" ? primaryColorLight : primaryColorDark,
            },
            error: { main: "#f44336" },
            warning: { main: "#ff9800" },
            success: { main: "#4caf50" },
            info: { main: "#2196f3" },
            background: {
              default: mode === "light" ? "#E8E8E8" : "#121212",
              paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
              contrast: mode === "light" ? "#F5F5F5" : "#2A2A2A",
              dark: mode === "light" ? "#E8E8E8" : "#2A2A2A",
            },
            text: {
              primary: mode === "light" ? "#212121" : "#FFFFFF",
              secondary: mode === "light" ? "#6E6B7B" : "#B4B7BD",
              disabled: mode === "light" ? "#9E9E9E" : "#757575",
            },
            divider:
              mode === "light"
                ? "rgba(0, 0, 0, 0.12)"
                : "rgba(255, 255, 255, 0.12)",
            action: {
              active:
                mode === "light"
                  ? "rgba(0, 0, 0, 0.54)"
                  : "rgba(255, 255, 255, 0.7)",
              hover:
                mode === "light"
                  ? "rgba(30, 58, 138, 0.08)" // #1E3A8A
                  : "rgba(96, 165, 250, 0.15)", // #60A5FA
              selected:
                mode === "light"
                  ? "rgba(30, 58, 138, 0.2)"
                  : "rgba(96, 165, 250, 0.25)",
            },
            overlay: {
              light: "rgba(255, 255, 255, 0.7)",
              medium: "rgba(255, 255, 255, 0.5)",
              dark: "rgba(255, 255, 255, 0.2)",
            },
            backdrop: {
              light:
                mode === "light" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.5)",
              medium:
                mode === "light" ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
              dark:
                mode === "light" ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.9)",
            },
            border: {
              light: mode === "light" ? "#E0E0E0" : "#333333",
              medium: mode === "light" ? "#BDBDBD" : "#424242",
              dark: mode === "light" ? "#9E9E9E" : "#616161",
              primary: mode === "light" ? primaryColorLight : primaryColorDark,
            },
            gradients: {
              primary: `linear-gradient(45deg, ${
                mode === "light" ? primaryColorLight : primaryColorDark
              } 30%, ${
                mode === "light" ? primaryColorLight : primaryColorDark
              } 90%)`,
              secondary: `linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)`,
              info: `linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)`,
              success: `linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)`,
              warning: `linear-gradient(45deg, #FF9800 30%, #FFC107 90%)`,
              error: `linear-gradient(45deg, #F44336 30%, #FF5252 90%)`,
            },
            shadow: {
              light:
                mode === "light"
                  ? "0 4px 14px 0 rgba(34, 41, 47, 0.1)"
                  : "0 4px 14px 0 rgba(0, 0, 0, 0.3)",
              medium:
                mode === "light"
                  ? "0 5px 15px rgba(34, 41, 47, 0.1)"
                  : "0 5px 15px rgba(0, 0, 0, 0.25)",
              dark:
                mode === "light"
                  ? "0 5px 10px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)"
                  : "0 5px 10px rgba(0,0,0,0.5), 0 6px 6px rgba(0,0,0,0.6)",
            },
          },
          borderRadius: {
            small: "5px",
            medium: "8px",
            large: "12px",
            circle: "50%",
          },
          typography: {
            fontFamily: getSystemFontFamily(systemFont),
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
          navbarHeight: navbarHeight || 56,
          navbarPosition: navbarPosition || "fixed",
          navbarBackground: navbarBackground || "",
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
      navbarHeight,
      navbarPosition,
      navbarBackground,
      systemFont,
    ]
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale =
      i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    if (isPublicWebchatPortal) return undefined;
    loadPublicBranding();
    return undefined;
  }, [isPublicWebchatPortal, loadPublicBranding]);

  useEffect(() => {
    if (isPublicWebchatPortal) return undefined;
    loadCompanyBrandingFromSession();
    return undefined;
  }, [isPublicWebchatPortal, loadCompanyBrandingFromSession]);

  useEffect(() => {
    if (isPublicWebchatPortal) return undefined;
    const handleAuthLogin = () => {
      loadCompanyBrandingFromSession();
    };
    const handleAuthRestore = () => {
      loadCompanyBrandingFromSession();
    };
    const handleAuthLogout = () => {
      loadPublicBranding();
    };

    window.addEventListener("auth:login-success", handleAuthLogin);
    window.addEventListener("auth:session-restored", handleAuthRestore);
    window.addEventListener("auth:logout", handleAuthLogout);

    return () => {
      window.removeEventListener("auth:login-success", handleAuthLogin);
      window.removeEventListener("auth:session-restored", handleAuthRestore);
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [isPublicWebchatPortal, loadCompanyBrandingFromSession, loadPublicBranding]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    const root = document.documentElement;
    const p = theme.palette || {};
    root.style.setProperty(
      "--toast-bg",
      (p.background && p.background.paper) ||
        (mode === "light" ? "#FFFFFF" : "#1E1E1E")
    );
    root.style.setProperty(
      "--toast-text",
      (p.text && p.text.primary) || (mode === "light" ? "#212121" : "#FFFFFF")
    );
    root.style.setProperty(
      "--surface-bg",
      (p.background && p.background.paper) ||
        (mode === "light" ? "#FFFFFF" : "#1E1E1E")
    );
    root.style.setProperty(
      "--surface-text",
      (p.text && p.text.primary) || (mode === "light" ? "#212121" : "#FFFFFF")
    );
    const borderColor =
      (p.border && p.border.medium) ||
      p.divider ||
      (mode === "light" ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.12)");
    root.style.setProperty("--toast-border", borderColor);
    root.style.setProperty("--surface-border", borderColor);
    const boxShadow =
      (p.shadow && p.shadow.medium) ||
      (mode === "light"
        ? "0 4px 14px 0 rgba(34, 41, 47, 0.1)"
        : "0 5px 15px rgba(0,0,0,0.25)");
    root.style.setProperty("--toast-shadow", boxShadow);
    root.style.setProperty("--surface-shadow", boxShadow);
    root.style.setProperty(
      "--color-success",
      (p.success && p.success.main) || "#4caf50"
    );
    root.style.setProperty(
      "--color-error",
      (p.error && p.error.main) || "#f44336"
    );
    root.style.setProperty(
      "--color-warning",
      (p.warning && p.warning.main) || "#ff9800"
    );
    root.style.setProperty(
      "--color-info",
      (p.info && p.info.main) || "#2196f3"
    );
  }, [theme, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        if (error?.response?.status !== 401 && error?.response?.status !== 403) {
          console.log("Error fetching data", error);
        }
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <link rel="icon" href={appLogoFavicon} />
        </Helmet>
        <ColorModeContext.Provider
          value={{
            toggleColorMode: colorMode.toggleColorMode,
            mode,
            setPrimaryColorLight: colorMode.setPrimaryColorLight,
            setPrimaryColorDark: colorMode.setPrimaryColorDark,
            setAppLogoLight: colorMode.setAppLogoLight,
            setAppLogoDark: colorMode.setAppLogoDark,
            setAppLogoFavicon: colorMode.setAppLogoFavicon,
            setAppName: colorMode.setAppName,
            setNavbarHeight: colorMode.setNavbarHeight,
            setNavbarPosition: colorMode.setNavbarPosition,
            setNavbarBackground: colorMode.setNavbarBackground,
            appLogoLight,
            appLogoDark,
            primaryColorLight,
            primaryColorDark,
            appName,
            appLogoFavicon,
          }}
        >
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <ActiveMenuProvider>
                <Routes />
              </ActiveMenuProvider>
            </QueryClientProvider>
            <ServiceWorkerUpdate />
            <QuotaExceededModal />
          </ThemeProvider>
        </ColorModeContext.Provider>
      </HelmetProvider>
    </>
  );
};

export default App;
